export const INTERNATIONAL_COUNTRY_CODE = "INTL"
export const DEFAULT_COUNTRY_CODE = INTERNATIONAL_COUNTRY_CODE
export const DEFAULT_CURRENCY_CODE = "USD"
export const COUNTRY_COOKIE_KEY = "lealti_country"
export const CURRENCY_COOKIE_KEY = "lealti_currency"
export const COUNTRY_MANUAL_COOKIE_KEY = "lealti_country_manual"

export const COUNTRY_OPTIONS = [
  { code: INTERNATIONAL_COUNTRY_CODE, name: "Internacional" },
  { code: "UY", name: "Uruguay" },
  { code: "AR", name: "Argentina" },
  { code: "BR", name: "Brasil" },
  { code: "CL", name: "Chile" },
  { code: "MX", name: "Mexico" },
  { code: "US", name: "Estados Unidos" },
  { code: "ES", name: "Espana" },
] as const

export const COUNTRY_TO_CURRENCY: Record<string, string> = {
  [INTERNATIONAL_COUNTRY_CODE]: DEFAULT_CURRENCY_CODE,
  UY: "UYU",
  AR: "ARS",
  BR: "BRL",
  CL: "CLP",
  MX: "MXN",
  US: "USD",
  ES: "EUR",
}

export const SUPPORTED_COUNTRY_CODES: ReadonlySet<string> = new Set<string>(
  COUNTRY_OPTIONS.map((option) => option.code)
)

const TIMEZONE_COUNTRY_RULES: Array<[RegExp, string]> = [
  [/^America\/Montevideo$/i, "UY"],
  [/^America\/Argentina\//i, "AR"],
  [/^America\/Santiago$/i, "CL"],
  [/^America\/(Sao_Paulo|Belem|Fortaleza|Recife|Maceio|Bahia|Manaus|Cuiaba|Campo_Grande|Porto_Velho|Boa_Vista|Rio_Branco)$/i, "BR"],
  [/^America\/(Mexico_City|Monterrey|Chihuahua|Tijuana|Cancun|Merida)$/i, "MX"],
  [/^Europe\/Madrid$/i, "ES"],
  [/^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Adak|Boise|Detroit|Indiana\/Indianapolis|Kentucky\/Louisville|North_Dakota\/Center)$/i, "US"],
  [/^Pacific\/Honolulu$/i, "US"],
]

const parseLocaleRegion = (locale: string | null | undefined): string | null => {
  if (!locale) return null

  if (typeof (Intl as any)?.Locale === "function") {
    try {
      const parsed = new Intl.Locale(locale)
      if (parsed?.region && /^[A-Za-z]{2}$/.test(parsed.region)) {
        return parsed.region.toUpperCase()
      }
    } catch {
      // ignore parse errors
    }
  }

  const match = locale.match(/[-_]([A-Za-z]{2})\b/)
  if (!match?.[1]) return null
  return match[1].toUpperCase()
}

export const normalizeSelectableCountryCode = (
  value: unknown
): string | null => {
  if (typeof value !== "string") return null
  const normalized = value.trim().toUpperCase()
  if (!normalized) return null
  if (normalized === INTERNATIONAL_COUNTRY_CODE || normalized === "INTERNATIONAL") {
    return INTERNATIONAL_COUNTRY_CODE
  }
  if (!/^[A-Z]{2}$/.test(normalized)) return null
  return normalized
}

export const normalizeSupportedCountryCode = (
  value: unknown
): string | null => {
  const normalized = normalizeSelectableCountryCode(value)
  if (!normalized) return null
  if (SUPPORTED_COUNTRY_CODES.has(normalized)) return normalized
  return INTERNATIONAL_COUNTRY_CODE
}

export const normalizeCurrencyCode = (value: unknown): string | null => {
  if (typeof value !== "string") return null
  const normalized = value.trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(normalized)) return null
  return normalized
}

export const resolveMappedCurrencyForCountry = (
  countryCode: unknown
): string => {
  const normalized = normalizeSelectableCountryCode(countryCode)
  if (!normalized) return DEFAULT_CURRENCY_CODE
  return COUNTRY_TO_CURRENCY[normalized] || DEFAULT_CURRENCY_CODE
}

export const readCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") return null
  const entries = document.cookie.split(";")
  for (const entry of entries) {
    const trimmed = entry.trim()
    if (!trimmed.startsWith(`${name}=`)) continue
    return decodeURIComponent(trimmed.slice(name.length + 1))
  }
  return null
}

export const hasCookieValue = (name: string): boolean => {
  return readCookieValue(name) !== null
}

export const readLocalStorageValue = (key: string): string | null => {
  if (typeof window === "undefined") return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const normalizeBooleanFlag = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value
  if (typeof value !== "string") return null
  const normalized = value.trim().toLowerCase()
  if (!normalized) return null
  if (["1", "true", "yes", "on"].includes(normalized)) return true
  if (["0", "false", "no", "off"].includes(normalized)) return false
  return null
}

export const resolveCountryFromLocales = (
  locales: Array<string | null | undefined>
): string | null => {
  for (const locale of locales) {
    const region = parseLocaleRegion(locale)
    if (!region) continue
    if (SUPPORTED_COUNTRY_CODES.has(region)) {
      return region
    }
  }
  return null
}

export const resolveCountryFromTimeZone = (
  timeZone: string | null | undefined
): string | null => {
  if (!timeZone) return null
  const candidate = timeZone.trim()
  if (!candidate) return null

  for (const [pattern, countryCode] of TIMEZONE_COUNTRY_RULES) {
    if (!pattern.test(candidate)) continue
    if (SUPPORTED_COUNTRY_CODES.has(countryCode)) {
      return countryCode
    }
  }

  return null
}

export const resolveAutoDetectedCountry = (): string => {
  if (typeof window === "undefined") {
    return DEFAULT_COUNTRY_CODE
  }

  const htmlLang =
    typeof document !== "undefined"
      ? document.documentElement?.lang || null
      : null
  const browserLocales =
    typeof navigator !== "undefined"
      ? [
          ...(Array.isArray(navigator.languages) ? navigator.languages : []),
          navigator.language,
        ]
      : []

  const byTimezone = resolveCountryFromTimeZone(
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : null
  )
  if (byTimezone && SUPPORTED_COUNTRY_CODES.has(byTimezone)) {
    return byTimezone
  }

  const byLocale = resolveCountryFromLocales([htmlLang, ...browserLocales])
  if (byLocale && SUPPORTED_COUNTRY_CODES.has(byLocale)) {
    return byLocale
  }

  return DEFAULT_COUNTRY_CODE
}

export const resolveClientCountrySelection = (): string => {
  const persistedCountry = readClientPersistedCountry()
  const manualSelection = readClientCountryManualSelection()

  if (manualSelection && persistedCountry) {
    return persistedCountry
  }

  if (
    persistedCountry &&
    persistedCountry !== INTERNATIONAL_COUNTRY_CODE &&
    SUPPORTED_COUNTRY_CODES.has(persistedCountry)
  ) {
    return persistedCountry
  }

  const autoDetectedCountry = normalizeSupportedCountryCode(
    resolveAutoDetectedCountry()
  )
  if (autoDetectedCountry && SUPPORTED_COUNTRY_CODES.has(autoDetectedCountry)) {
    return autoDetectedCountry
  }

  if (persistedCountry && SUPPORTED_COUNTRY_CODES.has(persistedCountry)) {
    return persistedCountry
  }

  return DEFAULT_COUNTRY_CODE
}

export const resolveClientCurrencySelection = (
  countryCode?: string | null
): string => {
  const explicit =
    normalizeCurrencyCode(readCookieValue(CURRENCY_COOKIE_KEY)) ||
    normalizeCurrencyCode(readLocalStorageValue(CURRENCY_COOKIE_KEY))

  if (explicit) return explicit

  return resolveMappedCurrencyForCountry(countryCode || DEFAULT_COUNTRY_CODE)
}

export const readClientPersistedCountry = (): string | null => {
  const normalized =
    normalizeSelectableCountryCode(readCookieValue(COUNTRY_COOKIE_KEY)) ||
    normalizeSelectableCountryCode(readLocalStorageValue(COUNTRY_COOKIE_KEY))

  if (!normalized) return null
  return SUPPORTED_COUNTRY_CODES.has(normalized) ? normalized : null
}

export const readClientCountryManualSelection = (): boolean => {
  const cookieFlag = normalizeBooleanFlag(readCookieValue(COUNTRY_MANUAL_COOKIE_KEY))
  if (cookieFlag !== null) return cookieFlag
  const localFlag = normalizeBooleanFlag(readLocalStorageValue(COUNTRY_MANUAL_COOKIE_KEY))
  return localFlag === true
}

export const emitCountrySelectionEvent = (payload: {
  country: string
  currency?: string | null
}) => {
  if (typeof window === "undefined") return
  window.dispatchEvent(
    new CustomEvent("lealti:country", {
      detail: {
        country: payload.country,
        currency: payload.currency || undefined,
      },
    })
  )
}

export const emitCurrencySelectionEvent = (currency: string) => {
  if (typeof window === "undefined") return
  window.dispatchEvent(
    new CustomEvent("lealti:currency", {
      detail: { currency },
    })
  )
}
